import React from 'react';
// client/src/pages/Dashboard.js
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { isAuthenticated } from '../../infrastructure/utils/auth';
import {
  Container,
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Button,
  Grid,
  Paper,
  Tooltip,
  Zoom,
  LinearProgress
} from '@mui/material';
import {
  Logout as LogoutIcon,
  AccountCircle,
  History as HistoryIcon,
  PlayArrow,
  Stop,
  Settings as SettingsIcon,
  ManageSearch as ManageSearchIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Favorite as FavoriteIcon,
  Memory as MemoryIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import { useSnackbar } from 'notistack';
import api, { TRADE, removeAuthToken, SYMBOL_CONTROL } from '../../infrastructure/config/api';
import RunningTradesTable from '../components/running-trades-table';
import DailyPnlList from '../components/position-history/daily-pnl-list';
import WeeklyPnlChart from '../components/dashboard/weekly-pnl-chart';
import AccountOverviewCard from '../components/account-overview-card';

const canUseTradingControls = (user) => {
  const role = String(user?.role || '').toLowerCase();
  const username = String(user?.username || '').toLowerCase();

  if (role === 'admin' || role === 'founder') return true;
  return username === 'khanh' || username === 'bien';
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  // State management
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [totalPnl, setTotalPnl] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isStartingAll, setIsStartingAll] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [trades, setTrades] = useState([]);
  const [lastTradesUpdate, setLastTradesUpdate] = useState(null);
  
  const open = Boolean(anchorEl);
  const canManageTrading = canUseTradingControls(currentUser);
  
  // Memoize expensive calculations
  const userData = useMemo(() => ({
    id: currentUser?.id,
    name: currentUser?.name
  }), [currentUser]);

  // Fetch user data and trades
  const fetchTrades = useCallback(async () => {
    try {
      console.log('🔄 [Dashboard] Fetching running trades...');
      const response = await api.get(TRADE.RUNNING);
      setTrades(response.data?.data || []);

      // Trigger auto-refresh for child components (P&L chart, etc.)
      const timestamp = Date.now();
      setLastTradesUpdate(timestamp);
      console.log('✅ [Dashboard] Trades fetched, updating lastTradesUpdate:', new Date(timestamp).toLocaleTimeString());
      console.log('  - This should trigger P&L auto-refresh');
    } catch (error) {
      console.error('❌ [Dashboard] Error fetching trades:', error);
      enqueueSnackbar('Failed to load trades', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [enqueueSnackbar]);

  // Get current user from localStorage on component mount
  useEffect(() => {
    const loadUserData = () => {
      try {
        const userData = localStorage.getItem('user');
        const token = localStorage.getItem('token');

        console.log('🔍 Loading user data from localStorage...');
        console.log('  - User data:', userData);
        console.log('  - Token exists:', !!token);

        if (userData) {
          const parsedUser = JSON.parse(userData);
          console.log('✅ Parsed user:', parsedUser);
          console.log('  - User ID:', parsedUser?.id);
          console.log('  - Username:', parsedUser?.username);
          setCurrentUser(parsedUser);
        } else {
          console.log('⚠️ No user data in localStorage');
        }
      } catch (error) {
        console.error('❌ Error loading user data:', error);
        enqueueSnackbar('Error loading user data', { variant: 'error' });
      }
    };

    loadUserData();
    fetchTrades();

    // Set up auto-refresh interval
    console.log('⏰ [Dashboard] Setting up auto-refresh interval (30s)...');
    const intervalId = setInterval(() => {
      console.log('🔔 [Dashboard] Interval triggered, fetching trades...');
      fetchTrades();
    }, 30000); // Refresh every 30 seconds

    return () => {
      console.log('🧹 [Dashboard] Cleaning up auto-refresh interval');
      clearInterval(intervalId);
    };
  }, [enqueueSnackbar, fetchTrades]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSettings = () => {
    navigate('/settings');
    handleClose();
  };

  const handleStopAll = useCallback(async () => {
    if (!window.confirm('Are you sure you want to stop all trading pairs?')) {
      return;
    }
    
    try {
      setIsProcessing(true);
      setIsStartingAll(false);
      
      const response = await api.post(SYMBOL_CONTROL.SKIP_ALL, { 
        action: 'skip',
        timestamp: new Date().toISOString()
      });
      
      if (response.data.success) {
        enqueueSnackbar('Successfully stopped all trading pairs', { 
          variant: 'success',
          autoHideDuration: 3000
        });
        await fetchTrades();
      } else {
        throw new Error(response.data.message || 'Failed to stop trading pairs');
      }
    } catch (error) {
      console.error('Error stopping all pairs:', error);
      enqueueSnackbar(
        error.response?.data?.error || error.message || 'Failed to stop trading pairs', 
        { variant: 'error' }
      );
    } finally {
      setIsProcessing(false);
    }
  }, [enqueueSnackbar, fetchTrades]);

  const handleStartAll = useCallback(async () => {
    if (!window.confirm('Are you sure you want to start all trading pairs?')) {
      return;
    }
    
    try {
      setIsProcessing(true);
      setIsStartingAll(true);
      
      const response = await api.post(SYMBOL_CONTROL.SKIP_ALL, { 
        action: 'start',
        timestamp: new Date().toISOString()
      });
      
      if (response.data.success) {
        enqueueSnackbar('Successfully started all trading pairs', { 
          variant: 'success',
          autoHideDuration: 3000
        });
        await fetchTrades();
      } else {
        throw new Error(response.data.message || 'Failed to start trading pairs');
      }
    } catch (error) {
      console.error('Error starting all pairs:', error);
      enqueueSnackbar(
        error.response?.data?.error || error.message || 'Failed to start trading pairs', 
        { variant: 'error' }
      );
    } finally {
      setIsProcessing(false);
    }
  }, [enqueueSnackbar, fetchTrades]);

  const handleLogout = useCallback(() => {
    try {
      console.log('🚪 Đang đăng xuất, clearing tất cả states...');

      // Clear all component states trước khi logout
      setTrades([]);
      setTotalPnl(0);
      setCurrentUser(null);
      setLastTradesUpdate(null);
      setIsLoading(true);

      // Clear authentication
      removeAuthToken();
      localStorage.removeItem('user');

      // Force page reload để clear tất cả component states và cache
      // Điều này đảm bảo không có data cũ nào còn sót lại
      console.log('✅ States cleared, redirecting to login...');
      window.location.href = '/login';
    } catch (error) {
      console.error('❌ Lỗi khi đăng xuất:', error);
      enqueueSnackbar('Lỗi khi đăng xuất', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  // Redirect to login if not authenticated
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  // Show loading state while data is being fetched
  if (isLoading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <React.Fragment>
      <AppBar position="static" elevation={1}>
        {/* Top Row */}
        <Toolbar 
          sx={{ 
            px: { xs: 1, sm: 2 },
            py: 1,
            borderBottom: '1px solid rgba(255, 255, 255, 0.12)'
          }}
        >
          {/* Left: App Name */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            flex: 1
          }}>
            <Typography 
              variant="h6" 
              component="h1"
              sx={{ 
                fontWeight: 600,
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                whiteSpace: 'nowrap'
              }}
            >
              Trading Bot
            </Typography>
          </Box>
          {/* Right: User Menu & History */}
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 1 }
          }}>
            <Tooltip title="Lịch sử giao dịch" TransitionComponent={Zoom} arrow>
              <IconButton
                color="inherit"
                onClick={() => navigate('/position-history')}
                size="small"
                sx={{
                  p: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.08)'
                  }
                }}
              >
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Favorites" TransitionComponent={Zoom} arrow>
              <IconButton
                color="inherit"
                onClick={() => navigate('/favorites')}
                size="small"
                sx={{
                  p: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.08)'
                  }
                }}
              >
                <FavoriteIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {currentUser?.role === 'admin' && (
              <Tooltip title="Quản lý Symbols" TransitionComponent={Zoom} arrow>
                <IconButton
                  color="inherit"
                  onClick={() => navigate('/symbol-management')}
                  size="small"
                  sx={{
                    p: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.08)'
                    }
                  }}
                >
                  <ManageSearchIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {canManageTrading && (
              <Tooltip title="Engine Runs" TransitionComponent={Zoom} arrow>
                <IconButton
                  color="inherit"
                  onClick={() => navigate('/engine/runs')}
                  size="small"
                  sx={{
                    p: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.08)'
                    }
                  }}
                >
                  <MemoryIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {currentUser?.role === 'admin' && currentUser?.username === 'devk' && (
              <Tooltip title="Admin Actions" TransitionComponent={Zoom} arrow>
                <IconButton
                  color="inherit"
                  onClick={() => navigate('/admin-actions')}
                  size="small"
                  sx={{
                    p: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.08)'
                    }
                  }}
                >
                  <AdminPanelSettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title="Tài khoản" TransitionComponent={Zoom} arrow>
              <IconButton
                size="small"
                aria-label="Tài khoản"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
                sx={{
                  p: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.08)'
                  }
                }}
              >
                <AccountCircle fontSize="medium" />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>

        {/* Bottom Row: Action Buttons */}
        <Box sx={{ 
          px: { xs: 2, sm: 3 },
          py: 1,
          display: 'flex',
          justifyContent: 'center',
          gap: 2,
          backgroundColor: 'rgba(0, 0, 0, 0.1)'
        }}>
            {canManageTrading && (
              <Tooltip title="Bắt đầu tất cả" TransitionComponent={Zoom} arrow>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={isProcessing && isStartingAll ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <PlayArrow />
                  )}
                  onClick={handleStartAll}
                  disabled={isProcessing}
                  size="small"
                  sx={{
                    minWidth: 'auto',
                    px: { xs: 1.5, sm: 2 },
                    py: 0.75,
                    borderRadius: 2,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    fontWeight: 500,
                    textTransform: 'none',
                    '& .MuiButton-startIcon': {
                      margin: 0,
                      '& > *': {
                        fontSize: { xs: '1rem', sm: '1.25rem' }
                      }
                    },
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: 2,
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, ml: 0.5 }}>
                    {isProcessing && isStartingAll ? 'Đang xử lý...' : 'Bắt đầu'}
                  </Box>
                </Button>
              </Tooltip>
            )}
            {canManageTrading && (
              <Tooltip title="Dừng tất cả" TransitionComponent={Zoom} arrow>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={isProcessing && !isStartingAll ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <Stop />
                  )}
                  onClick={handleStopAll}
                  disabled={isProcessing}
                  size="small"
                  sx={{
                    minWidth: 'auto',
                    px: { xs: 1.5, sm: 2 },
                    py: 0.75,
                    borderRadius: 2,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    fontWeight: 500,
                    textTransform: 'none',
                    '& .MuiButton-startIcon': {
                      margin: 0,
                      '& > *': {
                        fontSize: { xs: '1rem', sm: '1.25rem' }
                      }
                    },
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: 2,
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, ml: 0.5 }}>
                    {isProcessing && !isStartingAll ? 'Đang xử lý...' : 'Dừng'}
                  </Box>
                </Button>
              </Tooltip>
            )}
        </Box>
        
        {/* User Menu */}
        <Menu
          id="menu-appbar"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          keepMounted
          PaperProps={{
            sx: {
              minWidth: '180px',
              mt: 1,
              '& .MuiMenuItem-root': {
                px: 2,
                py: 1.5,
                typography: 'body2',
                '& .MuiSvgIcon-root': {
                  mr: 1.5,
                  fontSize: '1.25rem',
                  color: 'text.secondary',
                },
              },
            },
          }}
          open={open}
          onClose={handleClose}
        >
          {currentUser && currentUser.role === 'founder' && (
            <MenuItem onClick={() => { navigate('/founder-settings'); handleClose(); }}>
              <SettingsIcon sx={{ mr: 1 }} /> Founder Settings
            </MenuItem>
          )}
          {currentUser && currentUser.role === 'admin' && (
            <MenuItem onClick={handleSettings}>
              <SettingsIcon sx={{ mr: 1 }} /> Cài đặt
            </MenuItem>
          )}
          {currentUser?.username === 'khanh' && (
            <MenuItem onClick={() => { navigate('/dashboard/symbols'); handleClose(); }}>
              <ManageSearchIcon sx={{ mr: 1 }} /> Quản lý Symbol
            </MenuItem>
          )}
          <MenuItem onClick={() => { navigate('/market-symbols'); handleClose(); }}>
            <ManageSearchIcon sx={{ mr: 1 }} /> 636 Symbols Coin
          </MenuItem>
          {currentUser && (currentUser.role === 'admin' || currentUser.role === 'founder') && (
            <MenuItem onClick={() => { navigate('/dego-scan'); handleClose(); }}>
              <MemoryIcon sx={{ mr: 1 }} /> DEGO Scan
            </MenuItem>
          )}
          {currentUser && (currentUser.role === 'admin' || currentUser.role === 'founder') && (
            <MenuItem onClick={() => { navigate('/atr-fibonacci-scan'); handleClose(); }}>
              <MemoryIcon sx={{ mr: 1 }} /> ATR Fib Retest Scan
            </MenuItem>
          )}
          {currentUser && (currentUser.role === 'admin' || currentUser.role === 'founder') && (
            <MenuItem onClick={() => { navigate('/tyler-scan'); handleClose(); }}>
              <MemoryIcon sx={{ mr: 1 }} /> Tyler Scan
            </MenuItem>
          )}
          {currentUser && (currentUser.role === 'admin' || currentUser.role === 'founder') && (
            <MenuItem onClick={() => { navigate('/rsi-scan'); handleClose(); }}>
              <MemoryIcon sx={{ mr: 1 }} /> RSI Scan
            </MenuItem>
          )}
          {currentUser && (currentUser.role === 'admin' || currentUser.role === 'founder') && (
            <MenuItem onClick={() => { navigate('/engine/learning?symbol=ETHUSDT'); handleClose(); }}>
              <SchoolIcon sx={{ mr: 1 }} /> Learning Dashboard
            </MenuItem>
          )}
          {currentUser && (currentUser.role === 'admin' || currentUser.role === 'founder') && (
            <MenuItem onClick={() => { navigate('/kronos'); handleClose(); }}>
              <MemoryIcon sx={{ mr: 1 }} /> Kronos Server
            </MenuItem>
          )}
          {currentUser && currentUser.role === 'admin' && currentUser.username === 'devk' && (
            <MenuItem onClick={() => { navigate('/v18-inspector'); handleClose(); }}>
              <ManageSearchIcon sx={{ mr: 1 }} /> V18 Inspector
            </MenuItem>
          )}
          {currentUser && currentUser.role === 'admin' && currentUser.username === 'devk' && (
            <MenuItem onClick={() => { navigate('/vp-supertrend'); handleClose(); }}>
              <MemoryIcon sx={{ mr: 1 }} /> VP Supertrend Bot
            </MenuItem>
          )}
          {currentUser && (currentUser.role === 'admin' || currentUser.role === 'founder') && (
            <MenuItem onClick={() => { navigate('/open-orders'); handleClose(); }}>
              <ManageSearchIcon sx={{ mr: 1 }} /> Open Orders
            </MenuItem>
          )}
          {currentUser && (currentUser.role === 'admin' || currentUser.role === 'founder') && (
            <MenuItem onClick={() => { navigate('/high-roe'); handleClose(); }}>
              <ManageSearchIcon sx={{ mr: 1 }} /> ROE &lt; -500%
            </MenuItem>
          )}
          {currentUser && (currentUser.role === 'admin' || currentUser.role === 'holder') && (
            <MenuItem onClick={() => { navigate('/checkin'); handleClose(); }}>
              <ManageSearchIcon sx={{ mr: 1 }} /> Daily Check-in
            </MenuItem>
          )}
          <MenuItem onClick={handleLogout}>
            <LogoutIcon sx={{ mr: 1 }} /> Đăng xuất
          </MenuItem>
        </Menu>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
        <Grid container spacing={3}>
          {/* Account Overview Card - Full width on mobile, 1/3 on larger screens */}
          <Grid item xs={12} md={4}>
            {currentUser ? (
              <AccountOverviewCard
                userId={userData.id}
                totalPnl={totalPnl}
                onUpdate={fetchTrades}
              />
            ) : (
              <Paper sx={{ p: 3, height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Typography>Loading user data...</Typography>
              </Paper>
            )}
          </Grid>

          {/* Weekly PnL Chart - Full width on mobile, 2/3 on larger screens */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '200px' }}>
              <WeeklyPnlChart
                onViewHistory={() => navigate('/position-history')}
                lastTradesUpdate={lastTradesUpdate}
              />
            </Paper>
          </Grid>

          {/* Running Trades Table - Full width */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
              <RunningTradesTable 
                trades={trades}
                onUpdate={fetchTrades}
                setTotalPnl={setTotalPnl} 
                currentUser={currentUser}
              />
            </Paper>
          </Grid>
          {/* Position History - Full width */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
              <DailyPnlList 
                isMobile={false} 
                onUpdate={fetchTrades}
              />
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </React.Fragment>
  );
};

export default Dashboard;
